import type { rilascioUi as it } from "../it/rilascioUi.ts";

/** La scheda Rilascio dell'interfaccia: percorso blue/green, controlli, instradamenti, CI. */
export const rilascioUi: typeof it = {
  "ci.titolo": "Automation",
  "ci.intro":
    "A secret URL that, when called with a tag, saves a revision with that tag and starts the stack. " +
    "Use it to deploy from the pipeline that just built the image, without handing anyone your Portman credentials.",
  "ci.copiaOra": "Copy this command now: the token won't be shown again.",
  "ci.conservaSegreto": "Store the token as a secret in your CI and replace the tag with the one you just built.",
  "ci.attivo": "active",
  "ci.token": (p: { indizio: string }) => `token ${p.indizio}…`,
  "ci.usatoDiRecente": "used recently",
  "ci.maiUsato": "never used",
  "ci.variabileTag": (p: { variabile: string }) =>
    `The received tag goes into the ${p.variabile} variable, which the compose uses to pick the image version. ` +
    "The URL can't be shown again: if you lost it, regenerate it — the old one stops working.",
  "ci.rigenera": "Regenerate",
  "ci.revoca": "Revoke",
  "ci.genera": "Generate token",
  "ci.ultimeChiamate": "Recent calls",
  "ci.accettata": "accepted",
  "ci.rifiutata": "rejected",

  "porteHost.correggi": "Fix",
  "porteHost.rimosse": "These lines will be removed:",
  "porteHost.aMano": (p: { servizi: string }) => `Still to fix by hand: ${p.servizi}.`,
  "porteHost.nulla": "There's nothing I can remove on my own.",
  "porteHost.revisione": "A new revision is created: you can go back to the current one from History.",
  "porteHost.rimuovi": "Remove and save",

  "rilascioUi.tornaRicreazione": "Back to recreate",
  "rilascioUi.passaBlueGreen": "Switch to blue/green",
  "rilascioUi.sezione.controlli": "Checks",
  "rilascioUi.sezione.strategia": "Strategy",
  "rilascioUi.sezione.instradamenti": "Routes",
  "rilascioUi.sezione.automazione": "Automation",
  "rilascioUi.titolo": "Zero-downtime deploy",
  "rilascioUi.intro":
    "The new version starts alongside the active one; traffic moves only once the new one responds. " +
    "If something goes wrong before the switch, traffic never moved.",
  "rilascioUi.passo": (p: { k: number; n: number }) => `step ${p.k} of ${p.n}`,
  "rilascioUi.attivo": "✓ Blue/green active: the next deploy won't interrupt the service",
  "rilascioUi.compatibile": (p: { n: number }) =>
    `✓ The compose is compatible (${p.n} ${p.n === 1 ? "check passed" : "checks passed"})`,
  "rilascioUi.apriConfigurazione": "Open configuration",
  "rilascioUi.controlli.titolo": "Pre-start checks",
  "rilascioUi.controlli.intro":
    "Tests the last saved revision the way a start will: variables, images, ports and container names.",
  "rilascioUi.instradamenti.titolo": "Routes",
  "rilascioUi.instradamenti.intro":
    "Each service's domain: Nginx Proxy Manager sends traffic to it, and in a blue/green deploy " +
    "moves it to the new version once it responds.",

  "percorso.strategia.titolo": "Choose the blue/green strategy",
  "percorso.strategia.perche": "With recreate, containers stop before the new ones start.",
  "percorso.proxy.titolo": "Connect Nginx Proxy Manager",
  "percorso.proxy.perche": "It's what moves traffic from the active version to the new one.",
  "percorso.instradamento.titolo": "Assign a domain to a service",
  "percorso.instradamento.perche": "It's the address that stays fixed while versions swap.",
  "percorso.porte.titolo": "Remove host ports",
  "percorso.porte.perche": "Two versions running together can't bind the same port.",
  "percorso.nomi.titolo": "Remove fixed container names",
  "percorso.nomi.perche": "Two versions running together can't share a container name.",
  "percorso.fissi-dominio.titolo": "Remove the domain from pinned services",
  "percorso.fissi-dominio.perche": "A pinned service doesn't change version, so there's no traffic to move.",
  "percorso.fissi-dipendenze.titolo": "Pin the dependencies of pinned services too",
  "percorso.fissi-dipendenze.perche": "A pinned service can't depend on one that changes with every deploy.",
  "percorso.reti-nome.titolo": "Remove fixed names from application networks",
  "percorso.reti-nome.perche": "A network with a fixed name would join the two versions.",
  "percorso.fissi-dati.titolo": "Move the data of pinned services",
  "percorso.fissi-dati.perche": "A service that becomes pinned would start empty, without the active version's data.",

  "rilascioUi.fissi": "Pinned: recreated only if their configuration changes",
  "rilascioUi.fisso": "pinned",
  "rilascioUi.instradato": "routed",
  "rilascioUi.portaDaIndicare": "internal port needed",
  "rilascioUi.portaInterna": (p: { porta: number }) => `internal port ${p.porta}`,
  "rilascioUi.piuPorte": "(declares more than one)",
  "rilascioUi.nonNelCompose": "no longer in the compose",
  "rilascioUi.modifica": "Edit",
  "rilascioUi.instrada": "Route",
  "rilascioUi.rimuovi": "Remove",
  "rilascioUi.dominio": "Domain",
  "rilascioUi.portaInternaCampo": "Internal port",
  "rilascioUi.dominioAiuto":
    "The proxy reads it from the request, it doesn't resolve it: it must resolve for whoever opens the browser. " +
    "Locally, name.localhost works with no setup. If the application builds its own links " +
    "from a variable — APP_URL and the like — use that domain, or requests after the first page will go elsewhere.",

  "proxy.installaNpm": "Install Nginx Proxy Manager",
  "proxy.portaHttp": "HTTP port",
  "proxy.portaHttps": "HTTPS port",
  "proxy.porteAiuto":
    "Host ports where the proxy receives traffic. The admin panel stays on 127.0.0.1:81, with credentials in the stack's environment.",
  "proxy.installa": "Install",
};
